import { z } from 'zod';
// Global support contact email, set by medflow_admin and surfaced to
// customers on the self-service pages (so non-editable fields can point
// them at support). Stored as a system_config value, not hardcoded, so it
// can change without a redeploy.
export const updateSupportEmailSchema = z.object({
    email: z.string().email().max(255),
});
// Support request lifecycle + triage disposition (issue #207). Kept in sync
// with the support_requests table enums.
export const SUPPORT_REQUEST_STATUSES = ['open', 'in_progress', 'completed'];
export const SUPPORT_REQUEST_RESOLUTIONS = ['merged_existing', 'new_issue', 'wont_fix'];
export const supportRequestStatusSchema = z.enum(SUPPORT_REQUEST_STATUSES);
export const supportRequestResolutionSchema = z.enum(SUPPORT_REQUEST_RESOLUTIONS);
// Submitted by any authenticated user. The email is pre-filled from the
// profile but editable per-request; phone is optional.
export const createSupportRequestSchema = z.object({
    subject: z.string().trim().min(1).max(255),
    message: z.string().trim().min(1).max(10_000),
    email: z.string().email().max(255),
    phone: z.string().trim().max(50).optional(),
});
// Where a support request originated. `central` is an in-app submission from a
// logged-in Central user (issue #207). The remaining tags are *forwarded*
// submissions ingested over the server-to-server seam (issue #251): the legacy
// Alpha/Omega monorepo apps and (when #225 lands) edge deployments. Central is
// the single triage store for all of them.
export const SUPPORT_REQUEST_SOURCES = ['central', 'alpha-legacy', 'omega-legacy', 'edge'];
export const supportRequestSourceSchema = z.enum(SUPPORT_REQUEST_SOURCES);
// Sources that arrive via the ingest endpoint (everything except in-app
// `central`). A forwarding client must tag itself with one of these.
export const SUPPORT_REQUEST_INGEST_SOURCES = ['alpha-legacy', 'omega-legacy', 'edge'];
export const supportRequestIngestSourceSchema = z.enum(SUPPORT_REQUEST_INGEST_SOURCES);
// Server-to-server ingest of a forwarded support request (issue #251). Unlike
// the in-app POST, the caller is an authenticated *proxy* (the monorepo REST
// Lambda / an edge node), not the end user, so the full submitter attribution
// travels in the body -- Central has no user row for these submitters. The
// client-generated `requestId` makes retries idempotent.
export const ingestSupportRequestSchema = z.object({
    requestId: z.string().uuid(),
    source: supportRequestIngestSourceSchema,
    // Free-text org label from the forwarding app; Central has no FK to match it
    // against, so it is stored for triage context only. Optional (staff/no-org).
    organization: z.string().trim().max(255).optional(),
    submitterName: z.string().trim().min(1).max(255),
    submitterEmail: z.string().email().max(255),
    submitterPhone: z.string().trim().max(50).optional(),
    subject: z.string().trim().min(1).max(255),
    message: z.string().trim().min(1).max(10_000),
});
// MedFlow SuperAdmin triage update. A request can be re-assigned, advanced
// through statuses, and -- when completed -- categorized with an optional
// follow-up URL and notes.
export const triageSupportRequestSchema = z
    .object({
    status: supportRequestStatusSchema.optional(),
    resolution: supportRequestResolutionSchema.nullable().optional(),
    followUpUrl: z.string().url().max(2048).nullable().optional(),
    resolutionNotes: z.string().trim().max(5000).nullable().optional(),
})
    .refine((v) => v.status !== undefined ||
    v.resolution !== undefined ||
    v.followUpUrl !== undefined ||
    v.resolutionNotes !== undefined, 'At least one field must be provided');
// Self-service email change (PATCH /api/users/me/email). Separated from the
// name-only profile edit because email is the login identity: it must be
// unique and is verified/audited separately.
export const updateOwnEmailSchema = z.object({
    email: z.string().email().max(255),
});
//# sourceMappingURL=support.js.map